<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  ActionItem,
  ActionsGroup,
  ActionsMenu,
  VBadge,
  VIcon,
  VLoadingSection,
  VButton,
} from '@/modules/shared/components'
import { Folder, useFilingStore } from '@/modules/filing/stores/filing-store'
import { downloadAndSave } from '@/modules/filing/utils/utils'
import { useActionsMenu } from '@/modules/shared/utils/actions-menu'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import TheUploader from './TheUploader.vue'
import { format } from '@/modules/shared/utils/v-table'
import { isNil } from 'lodash'

const props = defineProps<{
  folder: Folder
  isReadOnly?: boolean
  level: number
  isExpanded: boolean
  toggleExpand?: Function
  openFolder: Function
  onFolderDragStart: Function
  addFolder: Function
  editFolder: Function
  onItemDrop: Function
  onSelect: Function
  selectedKeys: string[]
}>()

const isDownloading = ref(false)
const filingStore = useFilingStore()
const authStore = useAuthStore()
const isSelected = computed(() => props.selectedKeys?.includes(props.folder.id))
const isDragOver = ref(false)
const isToggling = ref(false)
const isLoading = ref(false)
let dragCounter = 0

const handleToggleExpand = async () => {
  isToggling.value = true
  try {
    await props.toggleExpand()
  } finally {
    isToggling.value = false
  }
}

const handleDelete = async (id) => {
  if (confirm('Are you sure you want to delete this folder?')) {
    try {
      await filingStore.removeFolder(id)
    } catch (error) {
      console.error('Folder deletion failed:', error)
    }
  }
}

const handleDownload = async (id) => {
  try {
    isDownloading.value = true
    await downloadAndSave(filingStore.downloadFolder(id), 'downloaded-folder')
    isDownloading.value = false
  } catch (error) {
    isDownloading.value = false
    console.error('Folder download failed:', error)
  }
}

const handleDragEnter = () => {
  if (props.isReadOnly) return
  dragCounter++
  isDragOver.value = true
}

const handleDragLeave = () => {
  if (props.isReadOnly) return
  dragCounter--
  if (dragCounter === 0) {
    isDragOver.value = false
  }
}

const handleDrop = async (event: DragEvent, folder: Folder) => {
  if (props.isReadOnly) return
  dragCounter = 0
  isDragOver.value = false
  isLoading.value = true
  const is_success = await props.onItemDrop(event, folder)
  if (is_success && !props.isExpanded) await handleToggleExpand()
  isLoading.value = false
}

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
  if (props.isReadOnly) {
    event.dataTransfer.dropEffect = 'none'
  }
}

const permissionLabel = computed(() => {
  if (!props.folder.parent_id) return '--'
  if (props.folder.assigned_shareholders.length > 0) {
    return props.folder.assigned_shareholders.map((shareholder) => shareholder.name).join(', ')
  }
  return filingStore.accessLevelLabels(props.folder)
})

const canEdit = computed(() => {
  if (props.folder.system_folder === 'investor') return false
  if (authStore.is_site_or_group_admin) return true
  if (props.folder.fileable_type === 'InvestorSet' && ['admin', 'investor'].includes(props.folder.access_level)) {
    if (authStore.current_user?.managed_investor_set_ids?.includes(props.folder.fileable_id)) return true
    if (props.folder.access_level === 'investor' && props.folder.assigned_shareholders.length === 0) return true
    return props.folder.assigned_shareholders.find(
      (shareholder) => shareholder.type === 'Investor' && shareholder.id === authStore.current_user.investor_id,
    )
  }

  if (props.folder.fileable_type === 'Investor' && ['admin', 'investor'].includes(props.folder.access_level)) {
    if (authStore.current_user?.managed_individual_ids?.includes(props.folder.fileable_id)) return true
    if (props.folder.access_level === 'investor')
      return !!props.folder.assigned_shareholders.find(
        (shareholder) => shareholder.type === 'Investor' && shareholder.id === authStore.current_user.investor_id,
      )
  }
  return false
})

const actionsMenu = useActionsMenu(['update', 'delete', 'download'])
</script>

<template>
  <div
    class="tr"
    :class="{ expanded: isExpanded, selected: isSelected, 'drag-over': isDragOver }"
    @dblclick="openFolder(folder)"
    @click="onSelect(folder.id)"
    :data-folder-id="folder.id"
    :draggable="isReadOnly ? false : true"
    @dragstart="onFolderDragStart(folder)"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop($event, folder)"
    @dragover.prevent="handleDragOver"
  >
    <div class="td flex items-center space-x-1" :style="{ '--indentation': `${(level - 1) * 2}rem` }">
      <VIcon
        @click.stop="toggleExpand ? handleToggleExpand() : openFolder(folder)"
        name="ChevronDown"
        class="expand-icon h-4 w-4 transition-transform"
      />
      <VIcon :name="isExpanded ? 'FileFolderOpen' : 'FileFolderClosed'" size="18" />
      <div class="flex items-center space-x-2 px-1.5">
        <span>{{ folder.name }}</span>
        <VLoadingSection v-if="isToggling || isLoading" class="h-4 w-4" />
      </div>
    </div>
    <div class="td flex items-center">{{ format(folder.updated_at, 'date') }}</div>
    <div class="td flex items-center">
      <VBadge v-if="folder.parent_id" :class="level === 1 ? '' : 'opacity-75'">{{ permissionLabel }}</VBadge>
    </div>
    <div class="td flex items-center">
      <template v-if="folder.created_by === 'AUTOMATION USER (DO NOT DELETE)'"></template>
      <template v-else-if="isNil(folder.created_by_investor_id)">{{ folder.created_by }}</template>
      <template v-else>
        <RouterLink
          class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
          :to="{ name: 'investing.individual-overview', params: { individual_id: folder.created_by_investor_id } }"
          target="_blank"
          rel="noopener"
        >
          {{ folder.created_by }}
        </RouterLink>
      </template>
    </div>
    <div class="td flex items-center justify-end">
      <div class="flex items-center space-x-2">
        <VButton v-if="isReadOnly" @click="() => handleDownload(folder.id)" :loading="isDownloading" size="xs"
          >Download</VButton
        >
        <ActionsMenu v-else @click.stop @dblclick.stop>
          <ActionsGroup>
            <TheUploader
              v-if="!isReadOnly"
              :handleToggleExpand="handleToggleExpand"
              :isExpanded="props.isExpanded"
              :currentFolder="folder || null"
              as="action"
            />
            <ActionItem v-if="!isReadOnly" text="New Folder" @click="() => addFolder(folder)" />
            <ActionItem
              v-if="!isReadOnly"
              :disabled="!canEdit"
              :text="'Edit'"
              @click="() => editFolder(folder)"
              :loading="actionsMenu.actions.value.update?.loading"
            />
            <ActionItem
              :text="'Download'"
              @click="() => handleDownload(folder.id)"
              :loading="actionsMenu.actions.value.download?.loading"
            />
          </ActionsGroup>
          <ActionsGroup v-if="!isReadOnly">
            <ActionItem
              :text="'Delete'"
              @click="() => handleDelete(folder.id)"
              :loading="actionsMenu.actions.value.delete?.loading"
            />
          </ActionsGroup>
        </ActionsMenu>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tr {
  @apply col-span-5 grid grid-cols-5 border-b border-gray-100 hover:bg-gray-50;
  @apply [grid-template-columns:30%_20%_20%_20%_10%];
}

.tr.selected,
.tr.drag-over {
  @apply -mt-px border-t border-[#3b88af]/10 bg-[#3b88af]/10;
}

.td {
  @apply ml-4 px-4 py-3 pl-[var(--indentation,0)];
}

.tr .expand-icon {
  @apply -rotate-90 cursor-pointer transition-transform;
}

.tr.expanded .expand-icon {
  @apply rotate-0;
}
</style>
